import Button from '@/components/Button'
import { Stack } from '@/components/Layout'
import Text, { Title } from '@/components/Text'
import GsuLoader from '@/components/GsuLoader'
import dynamic from 'next/dynamic'
import * as styles from './style'

const VideoJSPlayer = dynamic(() => import('@/widgets/VideoJsPlayer'), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-full w-full absolute">
      <GsuLoader />
    </div>
  ),
})

interface Props {
  title?: string
  description?: string
  videoAsset?: {
    playbackUrlMP4?: string | null
    playbackUrlHLS?: string | null
    playbackUrlDash?: string | null
  }
  videoPoster?: string | null
  partnerLogo?: string
  partnerName?: string
  onDismiss: () => void
}

/**
 * Content for the partner welcome popup modal.
 * Renders video (optional), title, description, partner logo, and dismiss button.
 * Matches DialogAction styling patterns.
 */
const WelcomePopupContent = ({
  title,
  description,
  videoAsset,
  videoPoster,
  partnerLogo,
  partnerName,
  onDismiss,
}: Props): JSX.Element => {
  const hasVideo = !!(videoAsset?.playbackUrlMP4 || videoAsset?.playbackUrlHLS || videoAsset?.playbackUrlDash)

  return (
    <>
      {hasVideo && (
        <div className={styles.videoContainer}>
          <div className="aspect-video w-full relative">
            <VideoJSPlayer
              videoAsset={{
                playbackUrlMP4: videoAsset?.playbackUrlMP4 ?? undefined,
                playbackUrlHLS: videoAsset?.playbackUrlHLS ?? undefined,
                playbackUrlDash: videoAsset?.playbackUrlDash ?? undefined,
                videoPoster: videoPoster ?? undefined,
              }}
              videoJsPlayerOptions={{
                autoplay: false,
                bigPlayButton: true,
                controls: true,
                fill: true,
                muted: false,
                responsive: true,
                controlBar: {
                  pictureInPictureToggle: false,
                },
              }}
            />
          </div>
        </div>
      )}

      <Stack y alignItems="center" spaceBetweenY="md" className={hasVideo ? styles.contentWithVideo : styles.content}>
        {title ? (
          <Title size="lg" weight="lg" className={styles.title}>
            {title}
          </Title>
        ) : null}

        {description ? (
          <Text size="xs" tag="p" weight="sm" className="pt-xxs">
            <span dangerouslySetInnerHTML={{ __html: description }} />
          </Text>
        ) : null}

        {partnerLogo ? (
          <div className={styles.logo}>
            <img src={partnerLogo} alt={partnerName ?? 'Partner logo'} loading="lazy" />
          </div>
        ) : null}

        <div className={styles.buttons}>
          <Button label="Close" theme="primary" size="md" stretch onClick={onDismiss} className={styles.button} />
        </div>
      </Stack>
    </>
  )
}

export default WelcomePopupContent
